import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FirebaseSignInRequest,
  FirebaseSignInResponse,
  FirebaseErrorResponse,
} from "src/entities/v3/FirebaseSignInRequest";

interface UseFirebaseSignInProps {
  onSuccess?: (response: FirebaseSignInResponse) => void;
  onError?: (error: AxiosError<FirebaseErrorResponse>) => void;
}

export const useFirebaseSignIn = ({
  onSuccess,
  onError,
}: UseFirebaseSignInProps = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [t] = useTranslation();

  const signIn = useCallback(
    async (email: string, password: string) => {
      const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
      const tenantId = process.env.REACT_APP_FIREBASE_TENANT_ID;

      if (!apiKey) {
        enqueueSnackbar("Firebase API key not configured", {
          variant: "error",
        });
        return false;
      }

      setIsLoading(true);

      try {
        const requestBody: FirebaseSignInRequest = {
          email,
          password,
          returnSecureToken: true,
          ...(tenantId && { tenantId }),
        };

        const response = await axios.post<FirebaseSignInResponse>(
          `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`,
          requestBody,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (onSuccess) {
          onSuccess(response.data);
        }

        return true;
      } catch (error) {
        const axiosError = error as AxiosError<FirebaseErrorResponse>;

        let errorMessage = t("sign_in.fail");

        if (axiosError.response?.data?.error?.message) {
          const firebaseErrorMessage = axiosError.response.data.error.message;

          switch (firebaseErrorMessage) {
            case "EMAIL_NOT_FOUND":
              errorMessage = t("sign_in.error.email_not_found");
              break;
            case "INVALID_PASSWORD":
              errorMessage = t("sign_in.error.invalid_password");
              break;
            case "USER_DISABLED":
              errorMessage = t("sign_in.error.user_disabled");
              break;
            case "TOO_MANY_ATTEMPTS_TRY_LATER":
              errorMessage = t("sign_in.error.too_many_attempts");
              break;
            default:
              errorMessage = t("sign_in.fail");
          }
        }

        enqueueSnackbar(errorMessage, { variant: "error" });

        if (onError) {
          onError(axiosError);
        }

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, onError, enqueueSnackbar, t]
  );

  return {
    signIn,
    isLoading,
  };
};
